import { getShortMonthName } from './CommonUtils';
import {
  formatAbbreviatedMonthDay,
  formatAbbreviatedMonthDayRange,
  parseLocalDateOnly,
} from './DateUtils';
import {
  AUTO_GUARANTEE_SHIP_TYPES,
  DECORATING_EXPEDITED_DISABLE,
  DECORATING_FLASH_DISABLE,
  FLASH_SHIPPING,
  FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD,
  SHIPPING_DAYS,
  SHIPPING_DAYS_MIN,
  SHIPPING_METHOD_NOT_ELIGIBLE,
  getFlashDisableTooltip,
} from '@/constants/ShipTypes';
import { LACE_DECORATING_METHOD_ID, LACE_MTO_COLOR } from '@/constants/DecorMethods';

import type { ICartItemDetail, ITimelineElement } from '@/common/interfaces/cart.interface';
import type {
  IAdditionalShippingPriceRes,
  QuoterPayload,
} from '@/common/interfaces/quoter.interface';
import type { ShipType } from '@/common/interfaces/order.interface';

interface ShipTypeProperties {
  shipTypeKey: string;
  properties: Partial<ShipType>;
}

/**
 * Returns the weekened days count.
 *
 * @param startDate - The current date
 * @param days - The number of days
 */
export const countWeekendDays = (startDate: Date, days: number) => {
  const dateRange = Array.from({ length: days }, (_, index) => {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + index);

    return currentDate;
  });

  /** Use reduce to count weekend days */
  const count = dateRange.reduce((weekendCount, currentDate) => {
    const dayOfWeek = currentDate.getDay();

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return weekendCount + 1;
    }

    return weekendCount;
  }, 0);

  return count;
};

/**
 * Method to add days to the current date.
 *
 * @param daysToAdd - Number of days to add to the current date
 */
export const addDaysToDate = (
  daysToAdd: number,
  shipType: keyof typeof SHIPPING_DAYS,
  currentDate: Date,
  restockDate: string | null | undefined
) => {
  let result = new Date();
  if (restockDate) {
    result = currentDate;
  }
  let weekdaysToAdd = daysToAdd;

  /** If it flash shipping exclude weekends. */
  if (shipType === FLASH_SHIPPING || shipType === 'flash') {
    weekdaysToAdd = daysToAdd + countWeekendDays(result, daysToAdd);
  }

  result.setDate(result.getDate() + weekdaysToAdd);
  result.setHours(0, 0, 0);

  const dateInISOString = Number.isNaN(result.getTime()) ? '' : result.toISOString();

  const month = result.toLocaleString('default', { month: 'long' });

  return { dateInString: `${getShortMonthName(month)} ${result.getDate()}`, dateInISOString };
};

/** Function to get the current time in EST */
export const getCurrentTimeEST = (isoDateString?: string | null | undefined) => {
  if (isoDateString) {
    const utcDate = new Date(isoDateString);
    return new Date(
      utcDate.toLocaleString('en-US', {
        timeZone: 'America/New_York',
      })
    );
  }

  return new Date(
    new Date().toLocaleString('en-US', {
      timeZone: 'America/New_York',
    })
  );
};

/**
 * Method to get the current time in EST.
 *
 * @returns Estimated delivery time in EST
 */
export const getEstDeliveyTimeEST = (
  shipType: keyof typeof SHIPPING_DAYS,
  returnISOString?: boolean,
  restockDate?: string | null | undefined,
  getMinDateRange?: boolean
) => {
  const currentDate = getCurrentTimeEST(restockDate);
  let shipTypeDays = SHIPPING_DAYS[shipType];
  if (getMinDateRange) {
    shipTypeDays = SHIPPING_DAYS_MIN[shipType];
  }

  const daysToAdd = currentDate.getHours() >= 14 ? shipTypeDays + 1 : shipTypeDays;
  const { dateInString, dateInISOString } = addDaysToDate(
    daysToAdd,
    shipType,
    currentDate,
    restockDate
  );

  return returnISOString ? dateInISOString : dateInString;
};

/**
 * Method to display the shipping methods in a sorting order.
 *
 * @param shipTypeKey - The key used to sort the shipping methods. (Flash, MTO, Standard, Expedited)
 * @param shippingMethods - The shipping methods
 */
export const sortByShipTypeKey = (shipTypeKey: string, shippingMethods: ShipType[]): ShipType[] =>
  [...shippingMethods].sort((a, b) => {
    if (a.shipTypeKey === shipTypeKey) {
      return -1;
    }
    if (b.shipTypeKey === shipTypeKey) {
      return 1;
    }

    return 0;
  });

/**
 * This method will check if there are more than 3 colors in the cart items for screen printing
 * @param cartItems
 * @returns boolean
 */
export const hasMoreThanThreeColors = (cartItems: ICartItemDetail[]): boolean =>
  cartItems.some((item) => {
    const locations = item?.printTypeInfo;
    return locations?.some(
      (location) =>
        location?.numberOfColors &&
        location.numberOfColors > FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD &&
        location.printType?.id === 1
    );
  });

const normalizeShipTypeKey = (t: string | undefined): string => (t ?? '').toLowerCase();

/**
 * Priority for "Order now to get it by" on design pages: try standard, then mto, expedited, flash.
 */
export const SHIP_TYPE_DELIVERY_DISPLAY_PRIORITY = [
  'standard',
  'mto',
  'expedited',
  'flash',
] as const;

/**
 * Merge quoter rows that share a type: keep the row with max daysToBeAdded; eligible is false if any
 * row for that type has eligible === false.
 */
export const mergeAdditionalShippingRowsByTypeForDisplay = (
  rows: IAdditionalShippingPriceRes[]
): IAdditionalShippingPriceRes[] => {
  const byType = new Map<string, IAdditionalShippingPriceRes[]>();
  rows.forEach((r) => {
    const k = normalizeShipTypeKey(r.type);
    if (!k) return;
    let list = byType.get(k);
    if (!list) {
      list = [];
      byType.set(k, list);
    }
    list.push(r);
  });
  const merged: IAdditionalShippingPriceRes[] = [];
  byType.forEach((list) => {
    const anyIneligible = list.some((r) => r.eligible === false);
    const best = list.reduce((a, b) => ((a.daysToBeAdded ?? 0) >= (b.daysToBeAdded ?? 0) ? a : b));
    merged.push({
      ...best,
      eligible: anyIneligible ? false : best.eligible ?? true,
    });
  });
  return merged;
};

/** Flatten per-line quoter arrays (e.g. design group) into one list for merge + priority pick */
export const flattenGroupShippingObjectsToRows = (
  groupShippingObjects: Record<string, IAdditionalShippingPriceRes[] | unknown>
): IAdditionalShippingPriceRes[] => {
  const out: IAdditionalShippingPriceRes[] = [];
  Object.values(groupShippingObjects).forEach((arr) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((row) => {
      if (row && typeof row === 'object' && 'type' in row) {
        out.push(row as IAdditionalShippingPriceRes);
      }
    });
  });
  return out;
};

/**
 * Pick the first ship type in SHIP_TYPE_DELIVERY_DISPLAY_PRIORITY that is eligible; if none eligible,
 * fall back to the first type in priority that exists, then any row.
 */
export const pickEligibleShippingRowForDisplay = (
  rows: IAdditionalShippingPriceRes[] | undefined | null
): IAdditionalShippingPriceRes | undefined => {
  if (!rows?.length) return undefined;
  const merged = mergeAdditionalShippingRowsByTypeForDisplay(rows);
  const byType = new Map<string, IAdditionalShippingPriceRes>();
  merged.forEach((r) => {
    const k = normalizeShipTypeKey(r.type);
    if (k) byType.set(k, r);
  });
  const priorityRows = SHIP_TYPE_DELIVERY_DISPLAY_PRIORITY.map((type) => byType.get(type));
  const firstEligible = priorityRows.find(
    (row): row is IAdditionalShippingPriceRes => !!row && row.eligible !== false
  );
  if (firstEligible) return firstEligible;
  const firstExisting = priorityRows.find((row): row is IAdditionalShippingPriceRes => !!row);
  return firstExisting ?? merged[0];
};

/**
 * Delivery CTA dates ("Order now to get it by …"): when the design is MTO, prefer the **mto** row’s
 * `newTimeline` / `estDeliveryDate` even if `eligible === false`, so the message matches the MTO
 * timeline from the API. Otherwise use {@link pickEligibleShippingRowForDisplay} (first **eligible**
 * lane in standard → mto → expedited → flash), which skips ineligible rows — e.g. ineligible MTO
 * would otherwise cause standard (or another lane) dates to show instead.
 */
export const pickShippingRowForDeliveryDisplay = (
  rows: IAdditionalShippingPriceRes[] | undefined | null,
  isMtoDesign: boolean
): IAdditionalShippingPriceRes | undefined => {
  if (!rows?.length) return undefined;
  if (isMtoDesign) {
    const merged = mergeAdditionalShippingRowsByTypeForDisplay(rows);
    const mtoRow = merged.find((r) => normalizeShipTypeKey(r.type) === 'mto');
    if (mtoRow) return mtoRow;
  }
  return pickEligibleShippingRowForDisplay(rows);
};

export const getDeliveryIsoStringsFromTimelineElement = (
  row: ITimelineElement
): { maxIso: string; minIso: string; timelineLabel: string } => ({
  maxIso: row.newTimeline?.maxDate?.dateInISOString || row.estDeliveryDate?.dateInISOString || '',
  minIso: row.newTimeline?.minDate?.dateInISOString || '',
  timelineLabel: row.newTimeline?.timeline || row.timeline || '',
});

/**
 * Same priority as {@link pickShippingRowForDeliveryDisplay} for maxTimeline entries (design group).
 */
export const pickTimelineElementForDeliveryDisplay = (
  timeline: ITimelineElement[] | undefined | null,
  isMtoDesign: boolean
): ITimelineElement | undefined => {
  if (!timeline?.length) return undefined;
  if (isMtoDesign) {
    const mtoRow = timeline.find((r) => normalizeShipTypeKey(r.type) === 'mto');
    if (mtoRow) return mtoRow;
  }
  const byType = new Map<string, ITimelineElement>();
  timeline.forEach((r) => {
    const k = normalizeShipTypeKey(r.type);
    if (k) byType.set(k, r);
  });
  const priorityRows = SHIP_TYPE_DELIVERY_DISPLAY_PRIORITY.map((type) => byType.get(type));
  const firstEligible = priorityRows.find(
    (row): row is ITimelineElement => !!row && row.eligible !== false
  );
  if (firstEligible) return firstEligible;
  const firstExisting = priorityRows.find((row): row is ITimelineElement => !!row);
  return firstExisting ?? timeline[0];
};

/**
 * Whether a ship type is eligible according to maxTimeline rows.
 * - Empty `timeline` → **true** (no API data yet; do not disable lanes until loaded).
 * - Row missing for `shipTypeKey` when timeline is non-empty → **false** (ineligible).
 * - `eligible === false` → false.
 * - `eligible === true` or `eligible === undefined` on the row → true.
 */
export const isShipTypeEligibleInTimeline = (
  timeline: ITimelineElement[],
  shipTypeKey: string
): boolean => {
  if (!timeline.length) return true;
  const normalized = shipTypeKey.toLowerCase();
  const row = timeline.find((e) => (e.type ?? '').toLowerCase() === normalized);
  if (!row) return false;
  return row.eligible !== false;
};

/** Min/max ISO and optional flash-style label from the chosen quoter row */
export const getDeliveryIsoStringsFromShippingRow = (
  row: IAdditionalShippingPriceRes
): { maxIso: string; minIso: string; timelineLabel: string } => ({
  maxIso: row.newTimeline?.maxDate?.dateInISOString || row.estDeliveryDate?.dateInISOString || '',
  minIso: row.newTimeline?.minDate?.dateInISOString || '',
  timelineLabel: row.newTimeline?.timeline || row.timeline || '',
});

/**
 * Decor line shape for lace+MTO checks: modern API uses `decoratingMethod.id`;
 * legacy / quoter-style payloads use numeric `type` (see DecoratingMethod in quoter.interface).
 */
export interface ILaceMtoDecorMethodLine {
  decoratingMethod?: { id?: number };
  type?: number;
  color?: string;
}

/** True when any decorating line is Lace (id 3) with an MTO-only lace color. */
export const hasLaceMtoColorInDecorMethodInfo = (
  decorMethodInfo: ILaceMtoDecorMethodLine[] | undefined | null
): boolean =>
  !!decorMethodInfo?.some((method) => {
    const laceMethodId =
      typeof method.type === 'number' ? method.type : method.decoratingMethod?.id;
    return (
      laceMethodId === LACE_DECORATING_METHOD_ID && LACE_MTO_COLOR.includes(method.color || '')
    );
  });

/** This is to get the ship types state based on the validations * */
export const getValidShipMethodsInfo = (
  cartItems: ICartItemDetail[],
  currentSelectedShipType?: string
): {
  shipTypeProperties: ShipTypeProperties[];
  updateSelectedShipType: string;
  allShipTypesDisabled: boolean;
} => {
  const hasCustomNamesOrNumbers = cartItems.some(
    (item) => item.hasCustomNames || item.hasCustomNumbers
  );
  const hasDecorMethods = cartItems.some((item) =>
    item.isStreamlinedProofItem ? item.decorMethodInfo : item.decoratingMethod
  );
  const isFlashDesigns = cartItems.every((item) => item.isFlashEligible);
  const includesEngraving = cartItems.some((i) =>
    i.printTypeInfo?.some((p) => p?.printType?.id === 12)
  );
  const isMoreThan3Colors = hasMoreThanThreeColors(cartItems);

  /** Start with all ship types enabled; API eligibility + business rules will disable as needed */
  const shipTypeProperties: ShipTypeProperties[] = [
    { shipTypeKey: 'standard', properties: { hide: false, disable: false, tooltipMessg: '' } },
    { shipTypeKey: 'expedited', properties: { hide: false, disable: false, tooltipMessg: '' } },
    { shipTypeKey: 'flash', properties: { hide: false, disable: false, tooltipMessg: '' } },
    { shipTypeKey: 'mto', properties: { hide: false, disable: false, tooltipMessg: '' } },
  ];
  let updateSelectedShipType = '';

  /** 1. API eligibility: shipping-timeline maxTimeline only (same rules as {@link isShipTypeEligibleInTimeline}). */
  const sharedTimelineData = cartItems[0]?.timelineData ?? [];

  shipTypeProperties.forEach((shipTypeProp) => {
    const isIneligible =
      sharedTimelineData.length > 0 &&
      !isShipTypeEligibleInTimeline(sharedTimelineData, shipTypeProp.shipTypeKey ?? '');

    if (isIneligible) {
      shipTypeProp.properties = {
        ...shipTypeProp.properties,
        disable: true,
        tooltipMessg: SHIPPING_METHOD_NOT_ELIGIBLE,
      };
    }
  });

  /** 2. Business rules layered on top of API eligibility */

  /** Custom names/numbers or decorating methods → disable flash and expedited */
  if (hasCustomNamesOrNumbers || hasDecorMethods) {
    shipTypeProperties.forEach((s) => {
      if (s.shipTypeKey === 'flash') {
        s.properties = { ...s.properties, disable: true, tooltipMessg: DECORATING_FLASH_DISABLE };
      }
      if (s.shipTypeKey === 'expedited') {
        s.properties = {
          ...s.properties,
          disable: true,
          tooltipMessg: DECORATING_EXPEDITED_DISABLE,
        };
      }
    });
  }

  /** More than 3 colors or not all flash-eligible → disable flash */
  if (isMoreThan3Colors || !isFlashDesigns) {
    shipTypeProperties.forEach((s) => {
      if (s.shipTypeKey === 'flash') {
        s.properties = {
          ...s.properties,
          disable: true,
          tooltipMessg: getFlashDisableTooltip(isMoreThan3Colors, !isFlashDesigns),
        };
      }
    });
  }

  /** Engraving items → hide and disable everything except standard */
  if (includesEngraving) {
    shipTypeProperties.forEach((s) => {
      if (s.shipTypeKey !== 'standard') {
        s.properties = { ...s.properties, hide: true, disable: true };
      }
    });
  }

  /** 3. If the currently selected ship type is now disabled, fall back in priority order */
  const FALLBACK_ORDER = ['standard', 'mto', 'expedited', 'flash'] as const;
  const allShipTypesDisabled = shipTypeProperties.every(
    (s) => s.properties.disable || s.properties.hide
  );

  const selectedKey = normalizeShipTypeKey(currentSelectedShipType);
  const selectedProp = shipTypeProperties.find(
    (s) => normalizeShipTypeKey(s.shipTypeKey) === selectedKey
  );
  if (selectedProp?.properties.disable || selectedProp?.properties.hide) {
    const firstEnabled = FALLBACK_ORDER.find((key) => {
      const prop = shipTypeProperties.find((s) => s.shipTypeKey === key);
      return prop && !prop.properties.disable && !prop.properties.hide;
    });
    updateSelectedShipType = firstEnabled ?? '';
  }

  return { shipTypeProperties, updateSelectedShipType, allShipTypesDisabled };
};

/**
 * This method will check if there are more than 3 colors in the quoter request payload for screen printing
 * @param cartItems
 * @returns boolean
 */
export const hasMoreThanThreeColorsColorsQuoterPayload = (payload: QuoterPayload): boolean =>
  payload.apparels.some((item) =>
    item?.locations?.some(
      (location) =>
        location?.numberOfColors &&
        location.numberOfColors > FLASH_SHIPPING_ELIGIBLE_MAX_COLOR_THRESHOLD &&
        location.printingType === 1
    )
  );

/**
 * Ship type to quote for, given the user's selection and the colour count.
 *
 * The >3 screen-print colour rule only rules out flash, so only flash may be
 * downgraded. Downgrading every lane made the cart quote standard shipping
 * (and standard delivery dates) while the customer had expedited selected.
 */
export const resolveQuotableShipType = (
  selectedShipType: string,
  isMoreThan3Colors: boolean
): string => (isMoreThan3Colors && selectedShipType === 'flash' ? 'standard' : selectedShipType);

/**
 * This method gives the additional shipping type based on the conditions
 */
export const getAdditionalShippingType = (
  isFlashEligible: boolean,
  isMoreThan3Colors: boolean,
  includesMto: boolean
) => {
  let additionalShippingType;
  if (isFlashEligible && !isMoreThan3Colors) {
    additionalShippingType = 'flash';
  } else if (includesMto) {
    additionalShippingType = 'mto';
  } else {
    additionalShippingType = 'standard';
  }
  return additionalShippingType;
};

/**
 * Formats a single date string to the format "Month Day" (e.g., "Jul 15")
 * @param dateString - The date string to format (ISO string, date string, etc.)
 * @returns Formatted date string in "Month Day" format
 */
export const formatSingleDate = (dateString: string): string => {
  const parsedDate = parseLocalDateOnly(dateString) ?? new Date(dateString);

  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return formatAbbreviatedMonthDay(parsedDate);
};

const mapShippingRowTypeToEstKey = (type: string | undefined): keyof typeof SHIPPING_DAYS => {
  const t = (type ?? '').toLowerCase();
  if (t === 'flash') return 'flash';
  if (t === 'mto') return 'mto';
  if (t === 'expedited') return 'expedited';
  return 'standard';
};

const DELIVERY_RANGE_DAYS_TO_SUBTRACT = 3;

/**
 * Formats max/min delivery ISO dates into the CartButton-style "Order now to get it by" string.
 * When timeline changes are off (or min is missing / same day), returns a single date.
 */
export const formatEstDeliveryDateStrFromIso = (
  maxDateInput: string,
  minDateInput?: string,
  timelineChangesEnabled = false
): string => {
  const maxDateObj = parseLocalDateOnly(maxDateInput) ?? new Date(maxDateInput);
  if (Number.isNaN(maxDateObj.getTime())) {
    return '';
  }

  if (!timelineChangesEnabled) {
    return formatSingleDate(maxDateInput);
  }

  const minDateObj = minDateInput
    ? parseLocalDateOnly(minDateInput) ?? new Date(minDateInput)
    : new Date(maxDateObj);
  if (!minDateInput) {
    minDateObj.setDate(minDateObj.getDate() - DELIVERY_RANGE_DAYS_TO_SUBTRACT);
  }

  if (Number.isNaN(minDateObj.getTime())) {
    return formatSingleDate(maxDateInput);
  }

  const isSameDay =
    minDateObj.getFullYear() === maxDateObj.getFullYear() &&
    minDateObj.getMonth() === maxDateObj.getMonth() &&
    minDateObj.getDate() === maxDateObj.getDate();

  if (isSameDay) {
    return formatSingleDate(maxDateInput);
  }

  const maxShortMonth = getShortMonthName(maxDateObj.toLocaleString('default', { month: 'short' }));
  const minShortMonth = getShortMonthName(minDateObj.toLocaleString('default', { month: 'short' }));

  return maxShortMonth === minShortMonth
    ? `${maxShortMonth} ${minDateObj.getDate()} – ${maxDateObj.getDate()}`
    : `${minShortMonth} ${minDateObj.getDate()} – ${maxShortMonth} ${maxDateObj.getDate()}`;
};

/**
 * Derive cart/CTA delivery date string from quoter `additionalShippingPriceObject`
 * (same priority / formatting as CartButton).
 */
export const getEstDeliveryDateStrFromAdditionalShipping = (
  additionalShippingPriceObject: IAdditionalShippingPriceRes[] | undefined | null,
  isMto: boolean,
  timelineChangesEnabled = false
): string => {
  const row = pickShippingRowForDeliveryDisplay(additionalShippingPriceObject, isMto);
  if (!row) {
    return '';
  }

  const { maxIso, minIso } = getDeliveryIsoStringsFromShippingRow(row);
  if (maxIso) {
    return formatEstDeliveryDateStrFromIso(maxIso, minIso || undefined, timelineChangesEnabled);
  }

  const fallbackIso = getEstDeliveyTimeEST(mapShippingRowTypeToEstKey(row.type), true);
  return formatEstDeliveryDateStrFromIso(fallbackIso, undefined, timelineChangesEnabled);
};

/** Prefer the merged eligible expedited lane from quoter shipping rows. */
export const pickExpeditedShippingRow = (
  rows: IAdditionalShippingPriceRes[] | undefined | null
): IAdditionalShippingPriceRes | undefined => {
  if (!rows?.length) return undefined;
  const merged = mergeAdditionalShippingRowsByTypeForDisplay(rows);
  const expeditedRow = merged.find((r) => normalizeShipTypeKey(r.type) === 'expedited');
  if (!expeditedRow || expeditedRow.eligible === false) {
    return undefined;
  }
  return expeditedRow;
};

/**
 * Expedited delivery date string from quoter `additionalShippingPriceObject`
 * (used for cart payload `expeditedDeliveryDate`, not the CTA estimated date).
 * Returns undefined when expedited is missing or not eligible.
 */
export const getExpeditedDeliveryDateStrFromAdditionalShipping = (
  additionalShippingPriceObject: IAdditionalShippingPriceRes[] | undefined | null,
  timelineChangesEnabled = false
): string | undefined => {
  const row = pickExpeditedShippingRow(additionalShippingPriceObject);
  if (!row) {
    return undefined;
  }

  const { maxIso, minIso } = getDeliveryIsoStringsFromShippingRow(row);
  if (maxIso) {
    return formatEstDeliveryDateStrFromIso(maxIso, minIso || undefined, timelineChangesEnabled);
  }

  const fallbackIso = getEstDeliveyTimeEST('expedited', true);
  return formatEstDeliveryDateStrFromIso(fallbackIso, undefined, timelineChangesEnabled);
};

/**
 * Expedited delivery date from timeline (maxTimeline) or quoter shipping rows.
 * Timeline expedited row wins when present and eligible.
 * Returns undefined when expedited is missing or not eligible.
 */
export const getExpeditedDeliveryDateStr = ({
  additionalShippingPriceObject,
  timelineData,
  timelineChangesEnabled = false,
}: {
  additionalShippingPriceObject?: IAdditionalShippingPriceRes[] | null;
  timelineData?: ITimelineElement[] | null;
  timelineChangesEnabled?: boolean;
}): string | undefined => {
  if (timelineData?.length) {
    const timelineRow = timelineData.find((r) => normalizeShipTypeKey(r.type) === 'expedited');
    if (timelineRow) {
      if (timelineRow.eligible === false) {
        return undefined;
      }

      const { maxIso, minIso } = getDeliveryIsoStringsFromTimelineElement(timelineRow);
      if (maxIso) {
        return formatEstDeliveryDateStrFromIso(maxIso, minIso || undefined, timelineChangesEnabled);
      }
      return formatEstDeliveryDateStrFromIso(
        getEstDeliveyTimeEST('expedited', true),
        undefined,
        timelineChangesEnabled
      );
    }
  }

  return getExpeditedDeliveryDateStrFromAdditionalShipping(
    additionalShippingPriceObject,
    timelineChangesEnabled
  );
};

/**
 * Formats a delivery date range into a string like "Apr 14-16" or "Apr 30-May 2" if dates span different months
 * @param minDeliveryDateIso - The earliest delivery date in ISO string format
 * @param maxDeliveryDateIso - The latest delivery date in ISO string format
 * @returns Formatted delivery date range string
 */
export const formatDeliveryDateRange = (
  minDeliveryDateIso: string,
  maxDeliveryDateIso: string
): string => {
  const minDate = new Date(minDeliveryDateIso);
  const maxDate = new Date(maxDeliveryDateIso);

  if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) {
    return '';
  }

  return formatAbbreviatedMonthDayRange(minDate, maxDate);
};

export const getFormattedDeliveryDateRange = (
  deliveryTime: string,
  shipPriceObj: { timeline?: string; newTimeline?: { timeline: string } } | undefined,
  shipTypeKey: string
): string => {
  if (!shipPriceObj) {
    return deliveryTime;
  }

  if (shipTypeKey === 'flash') {
    return shipPriceObj.timeline ?? deliveryTime;
  }

  return shipPriceObj.newTimeline?.timeline ?? deliveryTime;
};

/** Merge per-item quoter shipping rows into one map keyed by ship type (max daysToBeAdded wins). */
export const mergeShipPriceObjFromCartItems = (
  items: ICartItemDetail[]
): { [key: string]: IAdditionalShippingPriceRes } | undefined => {
  const quoterAddiPriceObj: { [key: string]: IAdditionalShippingPriceRes } = {};

  items.forEach((item) => {
    item.additionalShippingPriceObject?.forEach((priceRow) => {
      if (quoterAddiPriceObj[priceRow.type]) {
        if (quoterAddiPriceObj[priceRow.type].daysToBeAdded < priceRow.daysToBeAdded) {
          quoterAddiPriceObj[priceRow.type] = priceRow;
        }
      } else {
        quoterAddiPriceObj[priceRow.type] = priceRow;
      }
    });
  });

  return Object.keys(quoterAddiPriceObj).length > 0 ? quoterAddiPriceObj : undefined;
};

/**
 * Business-days label for a ship method card — same resolution as legacy {@link ShippingMethod}.
 * Reads timeline / quoter data as-is; falls back to {@link ShipType.deliveryTime}.
 */
export const getShipMethodBusinessDaysLabel = (
  method: Pick<ShipType, 'shipTypeKey' | 'deliveryTime'>,
  options: {
    timelineData?: ITimelineElement[];
    shipPriceObj?: { [key: string]: IAdditionalShippingPriceRes };
    timelineChangesEnabled?: boolean;
  }
): string => {
  const { timelineData = [], shipPriceObj, timelineChangesEnabled = false } = options;
  const fallback = method.deliveryTime;

  if (!timelineChangesEnabled) {
    return shipPriceObj?.[method.shipTypeKey]?.timeline ?? fallback;
  }

  const timelineItem = timelineData.find((item) => item.type === method.shipTypeKey);

  if (!timelineItem) {
    return fallback;
  }

  const fromTimeline = !AUTO_GUARANTEE_SHIP_TYPES.includes(method.shipTypeKey)
    ? timelineItem.newTimeline?.timeline
    : timelineItem.timeline;

  return fromTimeline ?? fallback;
};

export const getCustomDeliveryDateRange = (estDateMin: string, estDateMax: string): string => {
  const minDate = new Date(estDateMin);
  const maxDate = new Date(estDateMax);

  if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) {
    throw new Error('Invalid date string provided to getCustomDeliveryDateRange');
  }

  const minMonth = minDate.getMonth() + 1;
  const minDay = minDate.getDate();
  const maxMonth = maxDate.getMonth() + 1;
  const maxDay = maxDate.getDate();

  return `${minMonth}/${minDay}–${maxMonth}/${maxDay}`;
};

/**
 * Adjusts a date to the next business day if it falls on a weekend, ensuring the result is in EST
 * @param date - The date to check and potentially adjust
 * @returns The adjusted date (next Monday if weekend, or original date if weekday) in EST
 */
export const adjustWeekendToNextBusinessDay = (date: Date): Date => {
  // Convert incoming date to EST first
  const estDate = getCurrentTimeEST(date.toISOString());

  // Clone the EST date
  const adjusted = new Date(estDate);
  const day = adjusted.getDay();

  // Saturday (6) -> +2, Sunday (0) -> +1
  if (day === 6) adjusted.setDate(adjusted.getDate() + 2);
  else if (day === 0) adjusted.setDate(adjusted.getDate() + 1);

  return adjusted;
};

export const getGuaranteeValue = (guarantee: string, shipType: string) => {
  let guaranteeValue = '100';
  const validGuaranteeValues = ['100', 'FREE'];

  if (AUTO_GUARANTEE_SHIP_TYPES.includes(shipType as (typeof AUTO_GUARANTEE_SHIP_TYPES)[number])) {
    guaranteeValue = 'FREE';
  } else {
    guaranteeValue = guarantee;
  }

  if (!validGuaranteeValues.includes(guaranteeValue)) {
    return null;
  }

  return guaranteeValue;
};
